import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react'
import CrossIcon from '@/assets/icons/cross.svg?react'
import RadioCheckedIcon from '@/assets/icons/radio-checked.svg?react'
import RadioOffIcon from '@/assets/icons/radio-off.svg?react'
import XTwitterIcon from '@/assets/icons/x-twitter-onboarding.svg?react'
import heroBgImage from '@/assets/images/onboarding-hero-bg.jpg'
import heroBgDarkImage from '@/assets/images/onboarding-hero-bg-dark.png'
import { Modal } from '@/components/base'
import { Trans } from '@/components/Trans'
import { cn } from '@/utils/style'

import { OnboardingTasksState } from './useOnboardingTasks'

function FollowXIcon() {
  return <XTwitterIcon className="w-[18px] h-[18px] shrink-0" />
}

interface OnboardingGuideModalProps {
  open: boolean
  tasks: OnboardingTasksState
  loading: boolean
  onClose: () => void
  onFollowXClick: () => void
  onDepositClick: () => void
}

export function OnboardingGuideModal({
  open,
  tasks,
  loading,
  onClose,
  onFollowXClick,
  onDepositClick,
}: OnboardingGuideModalProps) {
  const taskItems = [
    {
      key: 'followX',
      done: tasks.followXDone,
      label: (
        <Trans i18nKey="onboarding.tasks.followX">
          Get free credits by following us on <FollowXIcon />
        </Trans>
      ),
      onClick: onFollowXClick,
    },
    {
      key: 'firstDeposit',
      done: tasks.firstDepositDone,
      label: (
        <Trans i18nKey="onboarding.tasks.firstDeposit">
          Make your first deposit to power your AI workloads
        </Trans>
      ),
      onClick: onDepositClick,
    },
  ]

  const doneCount = taskItems.filter((task) => task.done).length

  return (
    <Modal open={open} onClose={onClose} overlayClosable className="w-[480px] p-0 gap-0">
      <div className="relative flex items-center justify-center px-8 py-6 overflow-hidden">
        <img
          src={heroBgImage}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover dark:hidden"
        />
        <img
          src={heroBgDarkImage}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover hidden dark:block"
        />
        <p className="relative h6 text-black dark:text-white text-center">
          <Trans i18nKey="onboarding.title">🎉 Welcome to Glows.ai</Trans>
        </p>
      </div>

      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-4 rounded-lg bg-neutral-50 p-4">
          <div className="t-sm text-secondary">
            <p>
              <Trans i18nKey="onboarding.desc1">
                You're all set and ready to launch AI workloads.
              </Trans>
            </p>
            <p>
              <Trans i18nKey="onboarding.desc2">
                Complete these quick tasks to unlock credits, get support, and start deploying
                faster.
              </Trans>
            </p>
          </div>
          <div className="flex gap-1 h-2">
            {taskItems.map((task, index) => (
              <div
                key={task.key}
                className={cn(
                  'flex-1 h-full rounded',
                  loading
                    ? 'bg-neutral-100 animate-pulse'
                    : index < doneCount
                      ? 'bg-btn-secondary'
                      : 'bg-panel-gray-200 border border-neutral-200',
                )}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          {loading
            ? taskItems.map((task) => (
                <div key={task.key} className="flex items-center gap-2 pl-2 pr-1 py-3">
                  <div className="w-5 h-5 shrink-0 rounded-full bg-neutral-100 animate-pulse" />
                  <div className="flex-1 h-5 rounded bg-neutral-100 animate-pulse" />
                  <div className="w-5 h-5 shrink-0 rounded bg-neutral-100 animate-pulse" />
                </div>
              ))
            : taskItems.map((task) => (
                <div
                  key={task.key}
                  className={cn(
                    'flex items-center gap-2 pl-2 pr-1 py-3 rounded-lg',
                    task.done ? 'cursor-default' : 'cursor-pointer hover:bg-panel-gray-100',
                  )}
                  onClick={task.done ? undefined : task.onClick}
                >
                  {task.done ? (
                    <RadioCheckedIcon className="w-5 h-5 shrink-0 text-tertiary opacity-60" />
                  ) : (
                    <RadioOffIcon className="w-5 h-5 shrink-0 text-tertiary" />
                  )}
                  <span
                    className={cn(
                      'flex-1 flex items-center gap-1 flex-wrap t-sm',
                      task.done ? 'line-through text-tertiary opacity-60' : 'text-primary',
                    )}
                  >
                    {task.label}
                  </span>
                  <ChevronRightIcon
                    className={cn('w-5 h-5 shrink-0 text-tertiary', task.done && 'opacity-60')}
                  />
                </div>
              ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-2 right-2 p-1 rounded-[5px] bg-panel-gray-100/50 hover:bg-panel-gray-100 flex items-center justify-center cursor-pointer"
      >
        <CrossIcon className="w-3.5 h-3.5" />
      </button>
    </Modal>
  )
}
